import React, { useState, useRef, useEffect } from "react";
import { Box, Typography } from "../../../design-system";
import logoSite from "../../../../assets/brand/logo_site.svg";
import DemoVideoPicker from "./DemoVideoPicker";
import { getDemoShareSrc, DEMO_SRC_EVENT } from "./demoSource";
import MovieIcon from "@mui/icons-material/Movie";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import PersonIcon from "@mui/icons-material/Person";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import NumbersIcon from "@mui/icons-material/Numbers";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import SortByAlphaIcon from "@mui/icons-material/SortByAlpha";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckIcon from "@mui/icons-material/Check";
import PushPinIcon from "@mui/icons-material/PushPin";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ContentPasteGoIcon from "@mui/icons-material/ContentPasteGo";
import LanguageIcon from "@mui/icons-material/Language";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import EditIcon from "@mui/icons-material/Edit";
import HighlightIcon from "@mui/icons-material/Highlight";
import SegmentIcon from "@mui/icons-material/Segment";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";

/**
 * The REAL Cherrypick editor embedded on the home page (LEFT), with control buttons on the RIGHT
 * that DRIVE it (same-origin: a control clicks the matching real toolbar button by its text — the
 * matches below come from the actual draggableToolbar.jsx). Zoom +/-, corner drag to enlarge, a
 * Black-Cherry-styled chat, and a full breakdown of every button. Auth is guarded (userStore).
 */
// The demo's share source comes from demoSource (getDemoShareSrc): the Film Reel by default,
// or a library video the logged-in user picks via <DemoVideoPicker />.
const font = "Outfit, sans-serif";

type Item = { label: string; match: string; c: string; exact?: boolean; twice?: boolean; Icon?: React.ElementType; sym?: string };
const EMO: Item[] = [
  { label: "Positive", match: "Positive", c: "#22c55e", sym: "+" },
  { label: "Neutral", match: "Neutral", c: "#8a93a6", sym: "=" },
  { label: "Negative", match: "Negative", c: "#ff5a5a", sym: "−" },
];
const SPK: Item[] = ["A", "D", "E", "F", "G", "H"].map((s) => ({ label: s, match: s, c: "#6aa8ff", exact: true, Icon: PersonIcon }));
const TIME: Item[] = [
  { label: "Clip Time", match: "Clip Time", c: "#f5a524", Icon: WatchLaterIcon },
  { label: "Length", match: "Length", c: "#59d3c6", Icon: NumbersIcon },
  { label: "Show spaces", match: "Show", c: "#6aa8ff", Icon: VisibilityIcon },
  { label: "Hide spaces", match: "Hide", c: "#6aa8ff", Icon: VisibilityIcon },
];
const SORT: Item[] = [
  { label: "Chronological", match: "Chrono", c: "#6aa8ff", Icon: WatchLaterIcon },
  { label: "A → Z", match: "Alpha", c: "#6aa8ff", Icon: SortByAlphaIcon },
  { label: "Shortest first", match: "Duration", c: "#6aa8ff", Icon: AccessTimeIcon },
  { label: "Longest first", match: "Duration", c: "#6aa8ff", twice: true, Icon: AccessTimeIcon },
];
const FIND: Item[] = [
  { label: "Search", match: "Search", c: "#6aa8ff", Icon: SearchIcon },
  { label: "Questions", match: "?", c: "#c084fc", exact: true, sym: "?" },
  { label: "Numbers", match: "#", c: "#c084fc", exact: true, sym: "#" },
];
const MARK: Item[] = [
  { label: "Pen (highlight)", match: "Pen", c: "#f5a524", Icon: BorderColorIcon },
  { label: "Edit text", match: "Edit", c: "#59d3c6", Icon: EditIcon },
  { label: "Marks only", match: "Marks only", c: "#f5a524", Icon: HighlightIcon },
  { label: "Pin to top", match: "Pin to Top", c: "#59d3c6", Icon: PushPinIcon },
  { label: "Play next", match: "Play Next", c: "#22c55e", Icon: SkipNextIcon },
];
const AIROW: Item[] = [
  { label: "Segment: sentences", match: "Sentences", c: "#f5a524", Icon: SegmentIcon },
  { label: "AI Filters", match: "AI Filters", c: "#8b5cf6", Icon: AutoFixHighIcon },
  { label: "Black Cherry", match: "Black Cherry", c: "#c01f5a", Icon: AutoFixHighIcon },
  { label: "Paste AI Picks", match: "Paste AI Picks", c: "#8b5cf6", Icon: ContentPasteGoIcon },
];
const GROUPS: { label: string; items: Item[] }[] = [
  { label: "Filter by emotion", items: EMO },
  { label: "Filter by speaker", items: SPK },
  { label: "Time & length", items: TIME },
  { label: "Sort — order the clips", items: SORT },
  { label: "Find", items: FIND },
  { label: "Mark, highlight & edit", items: MARK },
  { label: "Re-cut & AI", items: AIROW },
];

// A thorough breakdown of every button — text from the real toolbar guide.
const HELP: { group: string; items: { Icon: React.ElementType; name: string; d: string }[] }[] = [
  { group: "Filter", items: [
    { Icon: FilterAltIcon, name: "Sentiment", d: "Positive, neutral, or negative moments." },
    { Icon: PersonIcon, name: "Speaker", d: "Show only one person's clips." },
    { Icon: WatchLaterIcon, name: "Clip Time", d: "Keep clips within a duration range." },
    { Icon: NumbersIcon, name: "Length", d: "Filter by transcript character count." },
    { Icon: VisibilityIcon, name: "Spaces", d: "Show / hide / isolate the silent gaps." },
    { Icon: SearchIcon, name: "Search", d: "Find every clip that says a word or phrase." },
    { Icon: AutoFixHighIcon, name: "AI Filters", d: "Filter by entities, keywords, phrases." },
  ]},
  { group: "Sort", items: [
    { Icon: WatchLaterIcon, name: "Chrono", d: "Recording order — start → end." },
    { Icon: SortByAlphaIcon, name: "Alpha", d: "Alphabetical by first word, A → Z." },
    { Icon: AccessTimeIcon, name: "Duration", d: "By clip length — shortest / longest first." },
  ]},
  { group: "Select & make your video", items: [
    { Icon: CheckIcon, name: "Select a clip", d: "Tick a box (or double-click the text). Numbers = play order." },
    { Icon: PushPinIcon, name: "Pin to top", d: "Jump your picks to the top, in order." },
    { Icon: SkipNextIcon, name: "Play Next", d: "Play your selected clips back-to-back." },
    { Icon: MovieIcon, name: "Export", d: "Stitch your picks into one video (MP4, audio, captions…)." },
    { Icon: DownloadIcon, name: "Download one", d: "Grab a single clip — the ↓ on its right." },
  ]},
  { group: "AI & more", items: [
    { Icon: AutoFixHighIcon, name: "Black Cherry", d: "Describe a reel — the AI picks & orders the clips." },
    { Icon: ContentPasteGoIcon, name: "Paste AI Picks", d: "Paste the AI's reply to apply it instantly." },
    { Icon: LanguageIcon, name: "Translate (soon)", d: "Coming soon — translate the transcript into another language." },
    { Icon: RestartAltIcon, name: "Reset All", d: "Clear every filter, search and sort at once." },
  ]},
  { group: "Fine-tune timing & split", items: [
    { Icon: AccessTimeIcon, name: "− / + on the IN row", d: "Trim or extend a clip's START by 0.1s. Extending STEALS time from the clip before it." },
    { Icon: AccessTimeIcon, name: "− / + on the OUT row", d: "Trim or extend the END by 0.1s. Extending pushes the next clip's start forward." },
    { Icon: AccessTimeIcon, name: "Teal vs amber times", d: "Teal = you nudged it by hand. Amber = auto-nudged by a neighbor's change." },
    { Icon: ContentCutIcon, name: "Split a clip", d: "Right-click a word → yellow ✂ → press Enter to split it into two. Esc cancels; Unsplit merges them back." },
  ]},
];
const HELP_COLORS = ["#e61d50", "#6aa8ff", "#22c55e", "#8b5cf6", "#f5a524"];
const QUICK = ["Punchy 30-second reel", "Only the positive moments", "The most viral clips"];
const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));

const HomeEtEmbed: React.FC = () => {
  const [inView, setInView] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [shareSrc, setShareSrc] = useState<string>(getDemoShareSrc());
  const [fs, setFs] = useState(false);
  const [editorH, setEditorH] = useState(560);
  const [zoom, setZoom] = useState(0.5);
  const [resizing, setResizing] = useState(false);
  const [flash, setFlash] = useState(false);
  const [toast, setToast] = useState("");
  const [chat, setChat] = useState<{ role: "you" | "cherry"; text: string }[]>([
    { role: "cherry", text: "Tell me the reel you want — I'll pick & order the clips right in the editor." },
  ]);
  const [chatIn, setChatIn] = useState("");
  const ref = useRef<HTMLDivElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const say = (m: string) => { setToast(m); window.clearTimeout((say as any)._t); (say as any)._t = window.setTimeout(() => setToast(""), 3000); };

  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { setInView(true); io.disconnect(); } }, { rootMargin: "400px" });
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  // The picker (and later K) repoint the demo via a window event; reload the iframe on change.
  useEffect(() => {
    const onSrc = (e: Event) => {
      const next = (e as CustomEvent).detail;
      if (typeof next === "string" && next) { setShareSrc(next); setLoaded(false); }
    };
    window.addEventListener(DEMO_SRC_EVENT, onSrc);
    return () => window.removeEventListener(DEMO_SRC_EVENT, onSrc);
  }, []);

  const src = inView ? shareSrc : "";

  const drive = (match: string, exact = false): boolean => {
    try {
      const doc = iframeRef.current?.contentDocument;
      if (!doc) { say("The editor is still loading — one sec."); return false; }
      const norm = (s: string | null) => (s || "").replace(/\s+/g, " ").trim();
      const btns = Array.from(doc.querySelectorAll("button, th, [role='button']")) as HTMLElement[];
      const ml = match.toLowerCase();
      const el = exact
        ? btns.find((b) => norm(b.textContent) === match)
        : btns.find((b) => norm(b.textContent).toLowerCase().includes(ml) || (b.getAttribute("title") || b.getAttribute("aria-label") || "").toLowerCase().includes(ml));
      if (el) { el.click(); setFlash(true); window.setTimeout(() => setFlash(false), 550); return true; }
      say(`Couldn't find “${match}” — tell me and I'll fix the match.`);
      return false;
    } catch (e) { say("The editor is still loading — try again in a second."); return false; }
  };
  const runItem = (b: Item) => { drive(b.match, b.exact); if (b.twice) window.setTimeout(() => drive(b.match, b.exact), 260); };

  // Make the clip thumbnails bigger/smaller: open the ET's "Video Width" popover and nudge its slider.
  const resizeClips = (delta: number) => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) { say("The editor is still loading — one sec."); return; }
    drive("Video thumbnail size"); // icon-only button, matched by its title
    window.setTimeout(() => {
      const d2 = iframeRef.current?.contentDocument;
      const w2 = iframeRef.current?.contentWindow as any;
      if (!d2 || !w2) return;
      const ranges = Array.from(d2.querySelectorAll('input[type="range"]')) as HTMLInputElement[];
      const slider = ranges.find((r) => Number(r.max) >= 300); // the Video Width slider (max ~400)
      if (!slider) { say("Opened clip size — drag the slider in the editor."); return; }
      const cur = Number(slider.value) || 200;
      const next = Math.max(Number(slider.min) || 30, Math.min(Number(slider.max) || 400, cur + delta));
      const setter = Object.getOwnPropertyDescriptor(w2.HTMLInputElement.prototype, "value")?.set;
      if (setter) setter.call(slider, String(next)); else slider.value = String(next);
      slider.dispatchEvent(new w2.Event("input", { bubbles: true }));
      slider.dispatchEvent(new w2.Event("change", { bubbles: true }));
      setFlash(true); window.setTimeout(() => setFlash(false), 500);
    }, 380);
  };

  // Drive the REAL Black Cherry: open its panel in the editor, inject the prompt, press Send.
  // The actual AI response + clip selection happens inside the editor (the real thing).
  const sendChat = (text?: string) => {
    const p = (text ?? chatIn).trim();
    if (!p) return;
    setChat((c) => [...c, { role: "you", text: p }]);
    setChatIn("");
    const doc = iframeRef.current?.contentDocument;
    if (!doc) { setChat((c) => [...c, { role: "cherry", text: "The editor is still loading — try again in a second." }]); return; }
    const norm = (s: string | null) => (s || "").replace(/\s+/g, " ").trim();
    const bc = (Array.from(doc.querySelectorAll("button")) as HTMLElement[]).find((b) => norm(b.textContent).includes("Black Cherry"));
    if (bc) bc.click();
    setFlash(true); window.setTimeout(() => setFlash(false), 600);
    window.setTimeout(() => {
      const d2 = iframeRef.current?.contentDocument;
      const w2 = iframeRef.current?.contentWindow as any;
      if (!d2 || !w2) return;
      const field = d2.querySelector('input[placeholder*="Cherry"], textarea[placeholder*="Cherry"]') as HTMLInputElement | HTMLTextAreaElement | null;
      if (!field) { setChat((c) => [...c, { role: "cherry", text: "Opened Black Cherry in the editor — type your request in its box." }]); return; }
      try {
        const proto = field.tagName === "TEXTAREA" ? w2.HTMLTextAreaElement.prototype : w2.HTMLInputElement.prototype;
        const setter = Object.getOwnPropertyDescriptor(proto, "value")?.set;
        (field as HTMLElement).focus();
        if (setter) setter.call(field, p); else (field as any).value = p;
        field.dispatchEvent(new w2.Event("input", { bubbles: true }));
        const send = (Array.from(d2.querySelectorAll("button")) as HTMLElement[]).find((b) => norm(b.textContent) === "Send");
        if (send) { send.click(); setChat((c) => [...c, { role: "cherry", text: "Sent to Black Cherry — watch it pick & order the clips in the editor →" }]); }
        else setChat((c) => [...c, { role: "cherry", text: "Opened Black Cherry — press Send in its box." }]);
      } catch (e) { setChat((c) => [...c, { role: "cherry", text: "Opened Black Cherry in the editor — type your request there." }]); }
    }, 850);
  };

  const startResize = (e: React.PointerEvent) => {
    e.preventDefault();
    setResizing(true);
    const sx0 = e.clientX, sy0 = e.clientY, sH = editorH, sZ = zoom;
    const move = (ev: PointerEvent) => {
      const d = ((ev.clientX - sx0) + (ev.clientY - sy0)) / 2;
      setEditorH(clamp(sH + (ev.clientY - sy0), 340, 1500));
      setZoom(clamp(sZ + d / 1600, 0.35, 1.1));
    };
    const up = () => { setResizing(false); window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const iframeStyle = (scale: number): React.CSSProperties => ({
    position: "absolute", top: 0, left: 0, width: `${100 / scale}%`, height: `${100 / scale}%`,
    transform: `scale(${scale})`, transformOrigin: "top left", border: "none", background: "#0b0a0f",
    pointerEvents: resizing ? "none" : "auto",
  });

  const titleBar = (isFs: boolean) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: "10px", px: "14px", py: "9px", borderBottom: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)", flexShrink: 0 }}>
      <Box sx={{ display: "flex", gap: "6px" }}>{["#ff5f57", "#febc2e", "#28c840"].map((c) => <Box key={c} sx={{ width: 11, height: 11, borderRadius: "50%", background: c, opacity: 0.55 }} />)}</Box>
      <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: "13px", color: "#f3f4f9" }}>Film Reel — the live editor</Typography>
      <Box sx={{ flex: 1 }} />
      {!isFs && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "2px", mr: "6px", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "7px", overflow: "hidden" }}>
          <Box component="button" onClick={() => setZoom((z) => clamp(z - 0.1, 0.35, 1.1))} title="Zoom out" sx={zoomBtn}>−</Box>
          <Typography sx={{ fontFamily: font, fontSize: "11px", color: "#c2c7d6", px: "6px", minWidth: 34, textAlign: "center" }}>{Math.round(zoom * 100)}%</Typography>
          <Box component="button" onClick={() => setZoom((z) => clamp(z + 0.1, 0.35, 1.1))} title="Zoom in" sx={zoomBtn}>+</Box>
        </Box>
      )}
      <Box component="button" onClick={() => setFs(!isFs)} title={isFs ? "Close" : "Open full screen"} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "5px", background: "transparent", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "7px", color: "#c2c7d6", fontFamily: font, fontSize: "11px", px: "8px", py: "4px", "&:hover": { color: "#fff", borderColor: "rgba(255,255,255,0.3)" } }}>
        {isFs ? "Close ×" : <><OpenInFullIcon sx={{ fontSize: 13 }} /> Full screen</>}
      </Box>
    </Box>
  );

  return (
    <Box ref={ref} sx={{ width: "100%" }}>
      <Box sx={{ mb: "16px" }}>
        <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: { xs: "24px", md: "32px" }, color: "#fff", letterSpacing: "-0.01em" }}>Try the CherryClipz Spreadsheet</Typography>
        <Typography sx={{ fontFamily: font, fontSize: "15px", color: "#c2c7d6", mt: "4px" }}>Tap a control on the right and <Box component="span" sx={{ color: "#fff", fontWeight: 700 }}>watch the clips change on the left</Box>. Click any clip to play it. No install, no login.</Typography>
      </Box>

      {/* Logged-in users can drive this live demo with any of their own library videos. */}
      <DemoVideoPicker />

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0,1.45fr) minmax(340px,1fr)" }, gap: "16px", alignItems: "start" }}>
        {/* editor */}
        <Box sx={{ position: "relative", minWidth: 0, borderRadius: "14px", overflow: "hidden", border: flash ? "1px solid #e61d50" : "1px solid rgba(255,255,255,0.12)", boxShadow: flash ? "0 0 0 3px rgba(230,29,80,0.35)" : "0 30px 80px -50px rgba(0,0,0,0.8)", background: "#0b0a0f", transition: "box-shadow .2s, border-color .2s" }}>
          {titleBar(false)}
          <Box sx={{ position: "relative", height: `${editorH}px` }}>
            {src ? <iframe ref={iframeRef} title="CherryClipz — live editor" src={src} allow="fullscreen; clipboard-write" onLoad={() => setLoaded(true)} style={iframeStyle(zoom)} /> : null}
            {!loaded && (
              <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "12px", background: "#0b0a0f" }}>
                <img src={logoSite} alt="" width={38} style={{ opacity: 0.85 }} />
                <Typography sx={{ fontFamily: font, fontSize: "13px", color: "#9aa0b4" }}>Loading the live editor…</Typography>
              </Box>
            )}
            <Box onPointerDown={startResize} title="Drag to enlarge the editor" sx={{ position: "absolute", right: 0, bottom: 0, width: 22, height: 22, cursor: "nwse-resize", background: "linear-gradient(135deg, transparent 50%, rgba(230,29,80,0.55) 50%)" }} />
          </Box>
        </Box>

        {/* control panel */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          {/* GET YOUR VIDEO */}
          <Box sx={{ border: "1px solid rgba(230,29,80,0.55)", borderRadius: "14px", background: "linear-gradient(160deg, rgba(230,29,80,0.16), rgba(255,255,255,0.02))", p: "16px" }}>
            <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: "13px", color: "#fff", mb: "12px" }}>Get your video — two ways</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px", mb: "12px" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "7px", flexShrink: 0 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: "9px", background: "#e61d50", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "18px", fontWeight: 800 }}>✓</Box>
                <Box component="span" sx={{ color: "#8d94a8" }}>→</Box>
                <Box component="button" onClick={() => drive("Export")} title="Open Export in the editor" sx={{ cursor: "pointer", border: "none", display: "flex", alignItems: "center", gap: "5px", px: "12px", py: "9px", borderRadius: "9px", background: "linear-gradient(100deg,#e07ad0,#c24fb0)", "&:hover": { filter: "brightness(1.12)" } }}><MovieIcon sx={{ fontSize: 16, color: "#fff" }} /><Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: "12.5px", color: "#fff" }}>Export</Typography></Box>
              </Box>
              <Typography sx={{ fontFamily: font, fontSize: "13px", color: "#f4e2ea" }}><Box component="span" sx={{ color: "#fff", fontWeight: 700 }}>Select &amp; Export</Box> → one stitched video.</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Box sx={{ flexShrink: 0, width: 36, height: 36, borderRadius: "9px", background: "#1b1a20", border: "1px solid rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" }}><DownloadIcon sx={{ fontSize: 19 }} /></Box>
              <Typography sx={{ fontFamily: font, fontSize: "13px", color: "#f4e2ea" }}><Box component="span" sx={{ color: "#fff", fontWeight: 700 }}>Download one</Box> → the ↓ on the right of any clip.</Typography>
            </Box>
          </Box>

          {/* CONTROLS */}
          <Box sx={{ border: "1px solid rgba(255,255,255,0.12)", borderRadius: "14px", background: "rgba(255,255,255,0.02)", p: "16px" }}>
            <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: "13px", color: "#fff" }}><Box component="span" sx={{ color: "#e61d50" }}>◀</Box> These drive the editor</Typography>
            <Typography sx={{ fontFamily: font, fontSize: "12px", color: "#8d94a8", mb: "4px" }}>Tap any of them and the clips react instantly. Full breakdown of every button is below.</Typography>
            <Typography sx={grpLbl}>Clip size — see your clips bigger</Typography>
            <Box sx={rowSx}>
              <Ctrl label="Bigger clips" c="#e061a8" Icon={AspectRatioIcon} onClick={() => resizeClips(70)} />
              <Ctrl label="Smaller clips" c="#8a93a6" sym="−" onClick={() => resizeClips(-70)} />
            </Box>
            {GROUPS.map((g) => (
              <Box key={g.label}>
                <Typography sx={grpLbl}>{g.label}</Typography>
                <Box sx={rowSx}>{g.items.map((b) => <Ctrl key={b.label} label={b.label} c={b.c} Icon={b.Icon} sym={b.sym} onClick={() => runItem(b)} />)}</Box>
              </Box>
            ))}
            <Box sx={{ mt: "14px", display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
              <Ctrl label="Reset all" c="#ff3b3b" Icon={RestartAltIcon} onClick={() => drive("Reset")} />
              <Box component="button" onClick={() => say("Translate is coming soon 🌍")} sx={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px", fontFamily: font, fontWeight: 700, fontSize: "12.5px", color: "#8d94a8", background: "rgba(255,255,255,0.03)", border: "1px dashed rgba(255,255,255,0.25)", borderRadius: "9px", px: "11px", py: "8px" }}><LanguageIcon sx={{ fontSize: 16 }} />Translate <Box component="span" sx={{ fontSize: "9px", fontWeight: 800, color: "#f5a524", border: "1px solid #f5a52488", borderRadius: "999px", px: "5px", py: "1px", ml: "2px" }}>SOON</Box></Box>
            </Box>
          </Box>

          {/* BLACK CHERRY — styled like the real panel */}
          <Box sx={{ borderRadius: "14px", overflow: "hidden", border: "1px solid rgba(192,31,90,0.6)", background: "#140b12" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", p: "12px 14px", background: "linear-gradient(100deg,#c01f5a,#7b2d9e)" }}>
              <img src={logoSite} alt="" width={22} />
              <Box>
                <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: "16px", color: "#fff", lineHeight: 1.1 }}>Ask Black Cherry AI</Typography>
                <Typography sx={{ fontFamily: font, fontSize: "11px", color: "rgba(255,255,255,0.8)" }}>type a reel — it runs in the editor →</Typography>
              </Box>
            </Box>
            <Box sx={{ p: "12px 14px" }}>
              <Box sx={{ display: "flex", gap: "6px", mb: "10px" }}>
                {["OpenAI", "Claude", "Gemini"].map((p, i) => <Box key={p} sx={{ fontFamily: font, fontSize: "11px", fontWeight: 700, px: "10px", py: "4px", borderRadius: "999px", color: i === 0 ? "#fff" : "#c9b6f5", background: i === 0 ? "linear-gradient(100deg,#8b5cf6,#6d28d9)" : "rgba(139,92,246,0.14)", border: "1px solid rgba(139,92,246,0.4)" }}>{p}</Box>)}
              </Box>
              <Box sx={{ maxHeight: 138, overflowY: "auto", display: "flex", flexDirection: "column", gap: "7px", mb: "10px" }}>
                {chat.map((m, i) => (
                  <Box key={i} sx={{ alignSelf: m.role === "you" ? "flex-end" : "flex-start", maxWidth: "88%", px: "10px", py: "7px", borderRadius: "10px", background: m.role === "you" ? "rgba(139,92,246,0.3)" : "rgba(255,255,255,0.06)" }}>
                    <Typography sx={{ fontFamily: font, fontSize: "12px", color: "#f3f4f9", lineHeight: 1.4 }}>{m.text}</Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mb: "9px" }}>
                {QUICK.map((q) => <Box key={q} component="button" onClick={() => sendChat(q)} sx={{ cursor: "pointer", fontFamily: font, fontSize: "11px", color: "#c9b6f5", background: "rgba(139,92,246,0.16)", border: "1px solid rgba(139,92,246,0.45)", borderRadius: "999px", px: "10px", py: "5px", "&:hover": { background: "rgba(139,92,246,0.3)", color: "#fff" } }}>{q}</Box>)}
              </Box>
              <Box sx={{ display: "flex", gap: "7px" }}>
                <input value={chatIn} onChange={(e) => setChatIn(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") sendChat(); }} placeholder="Tell Cherry what to build…" style={{ flex: 1, minWidth: 0, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "9px", color: "#f3f4f9", fontFamily: font, fontSize: "12.5px", padding: "9px 11px", outline: "none" }} />
                <Box component="button" onClick={() => sendChat()} sx={{ cursor: "pointer", border: "none", borderRadius: "9px", px: "14px", fontFamily: font, fontWeight: 800, fontSize: "12.5px", color: "#fff", background: "linear-gradient(100deg,#8b5cf6,#6d28d9)" }}>Send</Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* FULL BREAKDOWN — every button */}
      <Box sx={{ border: "1px solid rgba(255,255,255,0.1)", borderRadius: "14px", background: "rgba(255,255,255,0.015)", p: { xs: "16px", md: "22px" }, mt: "18px" }}>
        <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: "16px", color: "#fff", mb: "16px" }}>Every button, explained</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "10px 32px" }}>
          {HELP.map((g, gi) => { const gc = HELP_COLORS[gi % HELP_COLORS.length]; return (
            <Box key={g.group} sx={{ breakInside: "avoid" }}>
              <Typography sx={{ ...grpLbl, mt: "4px", color: gc }}>{g.group}</Typography>
              {g.items.map((it) => (
                <Box key={it.name} sx={{ display: "flex", gap: "11px", mb: "11px" }}>
                  <Box sx={{ flexShrink: 0, width: 30, height: 30, borderRadius: "8px", border: `1px solid ${gc}66`, background: `${gc}1e`, display: "flex", alignItems: "center", justifyContent: "center", color: gc }}><it.Icon sx={{ fontSize: 17 }} /></Box>
                  <Box>
                    <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: "13px", color: "#f3f4f9" }}>{it.name}</Typography>
                    <Typography sx={{ fontFamily: font, fontSize: "12px", color: "#9aa0b4", lineHeight: 1.45 }}>{it.d}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          ); })}
        </Box>
      </Box>

      {toast && <Box sx={{ position: "fixed", bottom: 84, left: "50%", transform: "translateX(-50%)", zIndex: 12500, px: "16px", py: "10px", borderRadius: "10px", background: "rgba(20,16,25,0.96)", border: "1px solid rgba(230,29,80,0.55)", color: "#f3f4f9", fontFamily: font, fontSize: "13px", boxShadow: "0 20px 50px -20px rgba(0,0,0,0.8)" }}>💡 {toast}</Box>}

      {fs && (
        <Box onClick={() => setFs(false)} sx={{ position: "fixed", inset: 0, zIndex: 13000, background: "rgba(4,3,7,0.72)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", p: { xs: "8px", md: "18px" } }}>
          <Box onClick={(e: React.MouseEvent) => e.stopPropagation()} sx={{ width: "100%", maxWidth: "1500px", height: "92vh", display: "flex", flexDirection: "column", background: "#0b0a0f", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "14px", overflow: "hidden" }}>
            {titleBar(true)}
            <Box sx={{ position: "relative", flex: 1, overflow: "hidden" }}>
              {/* scale to fit so the right-edge timecode − / + steppers are never cut off */}
              {src ? <iframe title="CherryClipz — full editor" src={src} allow="fullscreen; clipboard-write" style={iframeStyle(0.82)} /> : null}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const grpLbl = { fontFamily: font, fontWeight: 800, fontSize: "9.5px", letterSpacing: "0.12em", textTransform: "uppercase" as const, color: "#6b6675", mt: "13px", mb: "7px", display: "block" };
const rowSx = { display: "flex", flexWrap: "wrap" as const, gap: "7px" };
const zoomBtn = { cursor: "pointer", background: "transparent", border: "none", color: "#c2c7d6", fontFamily: font, fontSize: "15px", lineHeight: 1, px: "9px", py: "3px", "&:hover": { color: "#fff", background: "rgba(255,255,255,0.08)" } };
const Ctrl: React.FC<{ label: string; c: string; onClick: () => void; Icon?: React.ElementType; sym?: string }> = ({ label, c, onClick, Icon, sym }) => (
  <Box component="button" onClick={onClick} sx={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px", fontFamily: font, fontWeight: 700, fontSize: "12.5px", color: "#fff", background: `linear-gradient(135deg, ${c}3d, ${c}1a)`, border: `1px solid ${c}`, borderRadius: "9px", px: "11px", py: "8px", lineHeight: 1.2, transition: "all .12s", "&:hover": { background: `linear-gradient(135deg, ${c}5e, ${c}30)`, boxShadow: `0 0 0 1px ${c}` }, "&:active": { transform: "translateY(1px)" } }}>
    {sym ? <Box component="span" sx={{ color: c, fontWeight: 800, fontSize: "14px", lineHeight: 1 }}>{sym}</Box> : Icon ? <Box component="span" sx={{ display: "flex", color: c }}><Icon sx={{ fontSize: 16 }} /></Box> : null}
    {label}
  </Box>
);

export default HomeEtEmbed;
